import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as store from '../lib/clubStore';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

const EMPTY_FORM = { id: '', name: '', website: '', lat: '', lng: '', timezone: '' };

// Datalist id shared by the add + edit forms' timezone inputs.
const TZ_LIST_ID = 'club-timezones';

// Free-text input backed by a datalist of the venues we sell into. Free text
// (not a <select>) so a club in a zone we haven't listed can still be entered —
// clubStore validates against the browser's IANA database on save.
function TimezoneField({ value, onChange, id }) {
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>Timezone</label>
      <input
        id={id}
        list={TZ_LIST_ID}
        placeholder="Europe/Dublin"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// What the race-window writer will actually use for this club. An unset
// timezone is only harmless for a single-timezone country (IE/GB); anywhere
// else the writer skips the club's race windows entirely, so say so here
// rather than letting an admin discover it as missing forecasts (ra-h1yt.3).
function ClubTimezoneNote({ club }) {
  const { tz, source } = store.resolveClubTimezone(club);
  if (!tz) {
    return (
      <span style={{ color: '#c0392b', fontWeight: 600 }}>
        no timezone — race windows skipped
      </span>
    );
  }
  return (
    <span title={source === 'country' ? `Derived from country ${club.country}` : 'Set on the club'}>
      {tz}
      {source === 'country' && <span style={{ color: '#b8860b' }}> (from country)</span>}
    </span>
  );
}

export default function ClubManager() {
  const { isGlobalAdmin } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-club form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newClub, setNewClub] = useState(EMPTY_FORM);

  // Edit form — keyed by clubId so only one row is in edit mode at a time
  const [editingClubId, setEditingClubId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  // Show soft-deleted clubs (default hidden — admins opt in to triage)
  const [showDeleted, setShowDeleted] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await store.loadAllClubs();
      setClubs(data.clubs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const visibleClubs = useMemo(
    () => clubs.filter((c) => showDeleted || c.deleted !== true),
    [clubs, showDeleted],
  );
  const deletedCount = useMemo(
    () => clubs.filter((c) => c.deleted === true).length,
    [clubs],
  );

  if (!isGlobalAdmin) {
    return (
      <div className="page">
        <div className="error-banner">Access denied. GlobalAdmin only.</div>
      </div>
    );
  }

  const handleCreateClub = async (e) => {
    e.preventDefault();
    const { id, name, website, lat, lng, timezone } = newClub;
    if (!id || !name) { setError('Club ID and Name are required.'); return; }
    if (!/^[a-z0-9-]+$/.test(id)) { setError('Club ID must be lowercase letters, numbers, and hyphens only.'); return; }
    try {
      await store.createClub({
        id,
        name,
        website: website || undefined,
        lat: lat ? parseFloat(lat) : undefined,
        lng: lng ? parseFloat(lng) : undefined,
        timezone: timezone.trim() || undefined,
      });
      setNewClub(EMPTY_FORM);
      setShowAddForm(false);
      await reload();
    } catch (err) {
      setError('Error creating club: ' + err.message);
    }
  };

  const handleUpdateStatus = async (clubId, status) => {
    try {
      await store.updateClubStatus(clubId, status);
      setClubs((prev) => prev.map((c) => c.id === clubId ? { ...c, status } : c));
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  const startEdit = (club) => {
    setEditingClubId(club.id);
    setEditForm({
      id: club.id,
      name: club.name || '',
      website: club.website || '',
      lat: club.location?.lat != null ? String(club.location.lat) : '',
      lng: club.location?.lng != null ? String(club.location.lng) : '',
      timezone: club.timezone || '',
    });
  };

  const cancelEdit = () => {
    setEditingClubId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const { name, website, lat, lng, timezone } = editForm;
    if (!name) { setError('Name is required.'); return; }
    try {
      await store.updateClub(editingClubId, {
        name,
        website,
        lat: lat ? parseFloat(lat) : NaN,
        lng: lng ? parseFloat(lng) : NaN,
        timezone: timezone.trim(),
      });
      cancelEdit();
      await reload();
    } catch (err) {
      setError('Error updating club: ' + err.message);
    }
  };

  const handleSoftDelete = async (club) => {
    if (!window.confirm(
      `Remove club "${club.name || club.id}"?\n\n` +
      `This soft-deletes the club — it stays in Firestore and can be restored ` +
      `from the "Show deleted" view. The app and public lists will hide it.`,
    )) return;
    try {
      await store.softDeleteClub(club.id);
      await reload();
    } catch (err) {
      setError('Error removing club: ' + err.message);
    }
  };

  const handleRestore = async (club) => {
    try {
      await store.restoreClub(club.id);
      await reload();
    } catch (err) {
      setError('Error restoring club: ' + err.message);
    }
  };

  if (loading) return <div className="page"><p>Loading clubs...</p></div>;

  return (
    <>
      <PortalHeader />
      <datalist id={TZ_LIST_ID}>
        {store.COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz} />)}
      </datalist>
      <div className="page">
        <PageHeader backTo="/admin" backLabel="Admin" title="Clubs &amp; Seasons">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(true)}>
            + Add Club
          </button>
        </PageHeader>

      {error && (
        <div className="error-banner">
          {error}
          <button className="btn btn-sm" style={{ marginLeft: '0.5rem' }} onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          Show deleted ({deletedCount})
        </label>
      </div>

      {showAddForm && (
        <div className="section" style={{ marginBottom: '1rem' }}>
          <h2>New Club</h2>
          <form onSubmit={handleCreateClub} className="card-form">
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label className="form-label">ID</label>
                <input placeholder="ie-howth" value={newClub.id} onChange={(e) => setNewClub({ ...newClub, id: e.target.value })} autoFocus />
              </div>
              <div className="form-field">
                <label className="form-label">Name</label>
                <input placeholder="Howth Yacht Club" value={newClub.name} onChange={(e) => setNewClub({ ...newClub, name: e.target.value })} />
              </div>
              <div className="form-field">
                <label className="form-label">Website</label>
                <input placeholder="https://www.hyc.ie" value={newClub.website} onChange={(e) => setNewClub({ ...newClub, website: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label className="form-label">Latitude</label>
                <input type="number" step="0.0001" placeholder="53.3897" value={newClub.lat} onChange={(e) => setNewClub({ ...newClub, lat: e.target.value })} />
              </div>
              <div className="form-field form-field-narrow">
                <label className="form-label">Longitude</label>
                <input type="number" step="0.0001" placeholder="-6.0653" value={newClub.lng} onChange={(e) => setNewClub({ ...newClub, lng: e.target.value })} />
              </div>
              <TimezoneField
                id="new-club-timezone"
                value={newClub.timezone}
                onChange={(timezone) => setNewClub({ ...newClub, timezone })}
              />
              <div className="form-field" style={{ flex: 'none' }}>
                <label className="form-label">&nbsp;</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="btn btn-primary btn-sm">Create</button>
                  <button type="button" className="btn btn-sm" onClick={() => { setShowAddForm(false); setNewClub(EMPTY_FORM); }}>Cancel</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {visibleClubs.length === 0 && (
        <div className="empty-state">
          {clubs.length === 0
            ? 'No clubs yet. Add one above.'
            : 'No clubs match the current filter.'}
        </div>
      )}

      {visibleClubs.map((club) => {
        const isDeleted = club.deleted === true;
        const isEditing = editingClubId === club.id;
        return (
          <div
            key={club.id}
            className="section"
            style={{
              marginBottom: '0.75rem',
              opacity: isDeleted ? 0.55 : 1,
              borderLeft: isDeleted ? '3px solid #c0392b' : undefined,
              paddingLeft: isDeleted ? '0.5rem' : undefined,
            }}
          >
            {isEditing ? (
              <form onSubmit={handleSaveEdit} className="card-form">
                <div className="form-row">
                  <div className="form-field form-field-narrow">
                    <label className="form-label">ID</label>
                    <input value={editForm.id} disabled style={{ color: '#888' }} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} autoFocus />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Website</label>
                    <input placeholder="https://…" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field form-field-narrow">
                    <label className="form-label">Latitude</label>
                    <input type="number" step="0.0001" value={editForm.lat} onChange={(e) => setEditForm({ ...editForm, lat: e.target.value })} />
                  </div>
                  <div className="form-field form-field-narrow">
                    <label className="form-label">Longitude</label>
                    <input type="number" step="0.0001" value={editForm.lng} onChange={(e) => setEditForm({ ...editForm, lng: e.target.value })} />
                  </div>
                  <TimezoneField
                    id={`edit-club-timezone-${club.id}`}
                    value={editForm.timezone}
                    onChange={(timezone) => setEditForm({ ...editForm, timezone })}
                  />
                  <div className="form-field" style={{ flex: 'none' }}>
                    <label className="form-label">&nbsp;</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="submit" className="btn btn-primary btn-sm">Save</button>
                      <button type="button" className="btn btn-sm" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <h2 style={{ marginBottom: '0.15rem' }}>
                    {club.name || club.id}
                    {isDeleted && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#c0392b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Deleted
                      </span>
                    )}
                  </h2>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    <code>{club.id}</code>
                    {club.website && (
                      <>
                        {' · '}
                        <a href={club.website} target="_blank" rel="noreferrer" style={{ color: '#1a1a2e' }}>{club.website}</a>
                      </>
                    )}
                    {club.location && <> · {club.location.lat}, {club.location.lng}</>}
                    {' · '}
                    <ClubTimezoneNote club={club} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  {!isDeleted && (
                    <>
                      <select
                        value={club.status || 'coming_soon'}
                        onChange={(e) => handleUpdateStatus(club.id, e.target.value)}
                        style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem', border: '1px solid #ddd', borderRadius: '3px' }}
                      >
                        <option value="active">active</option>
                        <option value="coming_soon">coming soon</option>
                        <option value="archived">archived</option>
                      </select>
                      <Link to={`/clubs/${club.id}/seasons`} className="btn btn-sm">Seasons</Link>
                      <button className="btn btn-sm" onClick={() => startEdit(club)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleSoftDelete(club)}>Remove</button>
                    </>
                  )}
                  {isDeleted && (
                    <button className="btn btn-sm btn-primary" onClick={() => handleRestore(club)}>Restore</button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </>
  );
}
